import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import { PADDING_LEFT_COMPENSATION } from './TimelineElement';
import { TimelineFrameThumbnails } from './TimelineFrameThumbnails/TimelineFrameThumbnails';
import { WaveForm } from './WaveForm';

type ExtendContentMode = 'waveform' | 'thumbnails' | 'none';

interface TrimGhostLayerProps {
  /** Pre-drag snapshot of the element's extent on the timeline */
  snapshotGlobalTime: number;
  snapshotDuration: number;
  snapshotTrimStart: number;
  /** Live (buffered) drag values reflecting the current cut/extend state */
  currentGlobalTime: number;
  currentDuration: number;
  currentTrimStart: number;
  /** Source URL for drawing a waveform inside the extend region */
  waveformUrl: string;
  elementType: string;
  /** What to render inside the green extend region for this row */
  extendContentMode: ExtendContentMode;
  /** Bar's top offset within the track, in px */
  barTop: number;
  /** Bar's visible height, in px */
  barHeight: number;
  /** Story + video IDs for pulling the revived source frames */
  storyId?: string;
  videoId?: string;
  videoWidth?: number;
  videoHeight?: number;
}

export const TrimGhostLayer: React.FC<TrimGhostLayerProps> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const {
    snapshotGlobalTime,
    snapshotDuration,
    snapshotTrimStart,
    currentGlobalTime,
    currentDuration,
    currentTrimStart,
    waveformUrl,
    elementType,
    extendContentMode,
    barTop,
    barHeight,
    storyId,
    videoId,
    videoWidth,
    videoHeight,
  } = props;
  const timelineScale = videoCreator.timelineScale;

  // We deliberately do NOT call useWaveformResample here. That hook has a
  // side effect (sets resampledOriginalWaveForm = {status: 'loading'} on
  // every deps change) that corrupts the shared resampledOriginalWaveForm
  // status whenever the URL isn't the originalVideoAudioUrl. The story's
  // primary waveform is already loaded by other components; WaveForm reads
  // the ready resampledOriginalWaveForm directly when isOriginalVideo is set.

  const renderExtendContent = (sourceStart: number, sourceDuration: number) => {
    if (sourceDuration <= 0) return null;
    if (extendContentMode === 'waveform') {
      return (
        <WaveSlot data-trim-ghost-wave-slot>
          <WaveForm
            id={`trim-ghost-extend-${sourceStart.toFixed(2)}`}
            url={waveformUrl}
            type={elementType}
            trim_start={sourceStart.toFixed(3)}
            duration={sourceDuration.toFixed(3)}
            height={barHeight}
            customColor="#10b981"
            isOriginalVideo={elementType === 'video'}
          />
        </WaveSlot>
      );
    }
    if (extendContentMode === 'thumbnails' && storyId && videoId) {
      const widthPx = sourceDuration * timelineScale;
      return (
        <ThumbnailSlot>
          <TimelineFrameThumbnails
            storyId={storyId}
            videoId={videoId}
            trimStart={sourceStart}
            duration={sourceDuration}
            elementWidth={widthPx}
            height={barHeight}
            videoWidth={videoWidth}
            videoHeight={videoHeight}
          />
        </ThumbnailSlot>
      );
    }
    return null;
  };

  const snapshotEnd = snapshotGlobalTime + snapshotDuration;
  const currentEnd = currentGlobalTime + currentDuration;

  // Union bounds so the container spans both the pre-drag Bar AND any
  // extended (green) region beyond it.
  const unionStart = Math.min(snapshotGlobalTime, currentGlobalTime);
  const unionEnd = Math.max(snapshotEnd, currentEnd);

  const leftPx = PADDING_LEFT_COMPENSATION + unionStart * timelineScale;
  const widthPx = (unionEnd - unionStart) * timelineScale;

  // Offsets within the container (relative to unionStart).
  const sPx = (snapshotGlobalTime - unionStart) * timelineScale;
  const sEndPx = (snapshotEnd - unionStart) * timelineScale;
  const cPx = (currentGlobalTime - unionStart) * timelineScale;
  const cEndPx = (currentEnd - unionStart) * timelineScale;

  // Cut regions: inside snapshot, outside current kept window.
  const leftCutLeft = sPx;
  const leftCutWidth = Math.max(0, cPx - sPx);
  const rightCutLeft = cEndPx;
  const rightCutWidth = Math.max(0, sEndPx - cEndPx);

  // Extend regions: outside snapshot, inside current kept window.
  const leftExtendLeft = cPx;
  const leftExtendWidth = Math.max(0, sPx - cPx);
  const rightExtendLeft = sEndPx;
  const rightExtendWidth = Math.max(0, cEndPx - sEndPx);

  // Source time ranges exposed in the extend regions (for waveform drawing).
  // Left extend: source from [currentTrimStart, snapshotTrimStart).
  // Right extend: source from [snapshotTrimStart + snapshotDuration,
  //                           currentTrimStart + currentDuration).
  const leftExtendSourceStart = currentTrimStart;
  const leftExtendSourceDuration = Math.max(
    0,
    snapshotTrimStart - currentTrimStart,
  );
  const rightExtendSourceStart = snapshotTrimStart + snapshotDuration;
  const rightExtendSourceDuration = Math.max(
    0,
    currentTrimStart + currentDuration - rightExtendSourceStart,
  );

  return (
    <Container
      style={{
        left: leftPx,
        width: widthPx,
        top: barTop,
        height: barHeight,
      }}
    >
      {leftCutWidth > 0 && (
        <CutOverlay style={{ left: leftCutLeft, width: leftCutWidth }} />
      )}
      {rightCutWidth > 0 && (
        <CutOverlay style={{ left: rightCutLeft, width: rightCutWidth }} />
      )}
      {leftExtendWidth > 0 && (
        <ExtendRegion style={{ left: leftExtendLeft, width: leftExtendWidth }}>
          {renderExtendContent(leftExtendSourceStart, leftExtendSourceDuration)}
        </ExtendRegion>
      )}
      {rightExtendWidth > 0 && (
        <ExtendRegion
          style={{ left: rightExtendLeft, width: rightExtendWidth }}
        >
          {renderExtendContent(
            rightExtendSourceStart,
            rightExtendSourceDuration,
          )}
        </ExtendRegion>
      )}
    </Container>
  );
});

const Container = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 5;
`;

const CutOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(255, 99, 132, 0.55);
  border-radius: 4px;
`;

const ExtendRegion = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  overflow: hidden;
  border-radius: 4px;
  border: 1px dashed rgba(16, 185, 129, 0.9);
  /* Solid dark-gray fill so the emerald waveform has strong contrast to
     read against; a transparent green wash buried the wave. */
  background: #1f2937;
`;

const WaveSlot = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.85;
`;

const ThumbnailSlot = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: 0.85;
`;
